import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `/impersonation/$grantId` — where a WeTalk support hand-off lands (plan-002 RB-18, design
 * §15.7.8, resolves C13; ADR 0017 §7).
 *
 * The operator console opens `<this console>/impersonation/<grant id>#<token>` in a new tab. The
 * token is the credential of a support session for the member being helped, so this screen treats
 * it as identity code:
 *
 *   1. it reads the fragment and REMOVES IT FROM THE ADDRESS BAR AT ONCE (`history.replace`),
 *      before anything is awaited — so the token is not left in the history, in a bookmark, or on
 *      screen for a shoulder to read. A fragment is never sent to a server and never appears in a
 *      `Referer`; this keeps it out of the one place it would still sit;
 *   2. it redeems it EXACTLY ONCE (`redeemImpersonationHandoff`), guarded against React's
 *      development double-invocation, because the API rotates the token on first use and a second
 *      call would be refused;
 *   3. on success it forgets any cached session and opens the console, whose shell then shows the
 *      support banner — driven by the session, never by a flag set here;
 *   4. on refusal it says the link has expired or was used, and offers nothing that could retry
 *      with the same token.
 *
 * It sits OUTSIDE the shell, like log-in: the person arriving here has no session on this origin
 * yet, and the shell's guard would send them to log in.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { QUERY_KEY } from "../../api/query.js";
import { useGateway } from "../../api/provider.js";
import { Brand } from "../../shell/sidebar.js";
import { handoffTokenOf, impersonationGrantIdOf } from "./handoff-fragment.js";

/** What the screen is showing. Only `redeeming` is transient. */
export type HandoffPhase = "redeeming" | "malformed" | "refused" | "unavailable" | "failed";

const COPY: Readonly<Record<Exclude<HandoffPhase, "redeeming">, { title: string; dek: string }>> = {
  get malformed() { return {
    get title() { return portalText("core", "impersonation.this_support_link_is_incomplete_cfcaf3"); },
    get dek() { return portalText("core", "impersonation.the_link_that_opened_this_page_is_missing_part_of_what_it_needs_a_bd0bae") +
      portalText("core", "impersonation.start_the_session_again_from_their_console_5d1ab0"); },
  }; },
  get refused() { return {
    get title() { return portalText("core", "impersonation.this_support_link_has_expired_or_was_already_used_93a008"); },
    get dek() { return portalText("core", "impersonation.each_link_opens_one_support_session_once_within_a_minute_of_being_6f38e1") +
      portalText("core", "impersonation.support_to_start_again_03e381"); },
  }; },
  get unavailable() { return {
    get title() { return portalText("core", "impersonation.support_sessions_cannot_be_opened_here_306b70"); },
    get dek() { return portalText("core", "impersonation.this_console_is_not_connected_to_a_wetalk_service_that_can_open_a_89dfea") +
      portalText("core", "impersonation.nothing_has_been_changed_00ac77"); },
  }; },
  get failed() { return {
    get title() { return portalText("core", "impersonation.the_support_session_could_not_be_opened_b0dcc3"); },
    get dek() { return portalText("core", "impersonation.something_went_wrong_on_our_side_ask_wetalk_support_to_start_agai_bcc222"); },
  }; },
};

export function ImpersonationHandoffScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams({ strict: false }) as { readonly grantId?: string };
  // Read ONCE, at the first render: the fragment is gone from the location a moment later.
  const [handoff] = useState(() => ({
    token: handoffTokenOf(location.hash),
    grantId: impersonationGrantIdOf(params.grantId),
    redeem: gateway.redeemImpersonationHandoff,
  }));
  const [phase, setPhase] = useState<HandoffPhase>(() =>
    handoff.token === null || handoff.grantId === null
      ? "malformed"
      : handoff.redeem === undefined
        ? "unavailable"
        : "redeeming",
  );
  const started = useRef(false);
  const pathname = location.pathname;

  // A layout effect, so the fragment leaves the address bar before the first paint.
  useLayoutEffect(() => {
    // Once per mount, including under StrictMode's double effect: the token dies on first use.
    if (started.current) return;
    started.current = true;

    // Strip the fragment FIRST, whatever happens next.
    router.history.replace(pathname);

    const { token, grantId, redeem } = handoff;
    if (token === null || grantId === null || redeem === undefined) return;
    const open = async (): Promise<void> => {
      const outcome = await redeem({ impersonationGrantId: grantId, handoffToken: token });
      if (outcome.kind === "refused") {
        setPhase("refused");
        return;
      }
      // Whatever this tab believed about who is signed in is now wrong.
      queryClient.removeQueries({ queryKey: QUERY_KEY.authSession });
      await navigate({ to: "/dashboard", replace: true });
    };
    open().catch(() => {
      setPhase("failed");
    });
  }, [handoff, navigate, pathname, queryClient, router.history]);

  return (
    <div className="c-entry">
      <nav className="nav" aria-label={portalText("core", "auth.primary_a9a96e")}>
        <span className="nav-brand">
          <Brand />
        </span>
      </nav>
      <main className="c-entry-split">
        {phase === "redeeming" ? (
          <div className="c-deferred" role="status" aria-live="polite">
            <h1 className="c-deferred-title">{portalText("core", "impersonation.opening_a_wetalk_support_session_d4db95")}</h1>
            <p className="c-deferred-dek">{portalText("core", "impersonation.a_member_of_wetalk_support_is_about_to_act_on_this_account_everyt_a64cba")}</p>
          </div>
        ) : (
          <div className="c-deferred" role="alert">
            <h1 className="c-deferred-title">{COPY[phase].title}</h1>
            <p className="c-deferred-dek">{COPY[phase].dek}</p>
          </div>
        )}
      </main>
    </div>
  );
}
